/**
 * Atlas — the data contract (COORDINATOR-OWNED, do not redefine).
 *
 * This is the anti-divergence seam between the two build agents:
 *   - `atlas-data`  owns the offline location-pack pipeline + the pure scoring
 *                   engine. It PRODUCES `LocationPack`s and implements `scoring.ts`.
 *   - `atlas-game`  owns the DOM presentation (panorama viewer, guess map, the
 *                   reminiscence place card, hub). It CONSUMES this schema and
 *                   renders the fixture pack until the real pack is merged in.
 *
 * Agents may EXTEND this (add an optional field, a new pool tag) but must not
 * change an existing shape without raising it with the coordinator. The shapes
 * here are what let both halves be built in parallel without colliding.
 *
 * Licensing / provenance note (read `CONTRACT.md`): we ship the *facts*
 * (coordinates, place names, Street View `panoId`s) — never cached imagery.
 * Google's terms permit storing `panoId`/lat/lng/date indefinitely; they forbid
 * caching the panorama pixels. The panorama is always fetched live at play time.
 */

// ---------------------------------------------------------------------------
// Geography primitives
// ---------------------------------------------------------------------------

export type LatLng = { lat: number; lng: number };

export type Continent =
  | 'Africa'
  | 'Asia'
  | 'Europe'
  | 'North America'
  | 'South America'
  | 'Oceania';

export const CONTINENTS: readonly Continent[] = [
  'Africa',
  'Asia',
  'Europe',
  'North America',
  'South America',
  'Oceania',
] as const;

/**
 * Difficulty tier for a GENERATED regional location (world/continent/country/US
 * state). Curated Landmarks carry NO difficulty. Player-facing labels are warm,
 * non-stigmatizing, and journey-flavored — NOT "Easy/Hard":
 *   classic    → "Classic"    (town centers, signs — the default)
 *   explorer   → "Explorer"   (villages and country roads)
 *   adventurer → "Adventurer" (rural roads, open country)
 * The raw difficulty score is never shown; the tier is purely a pool-selection.
 */
export type Difficulty = 'classic' | 'explorer' | 'adventurer';

export const DIFFICULTIES: readonly Difficulty[] = ['classic', 'explorer', 'adventurer'] as const;

/** Player-facing label for a difficulty tier. */
export const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  classic: 'Classic',
  explorer: 'Explorer',
  adventurer: 'Adventurer',
};

// ---------------------------------------------------------------------------
// A single playable location
// ---------------------------------------------------------------------------

/**
 * One vetted, recognizable place. Authored by curation (the agent's knowledge of
 * famous places) and ENRICHED by the metadata pipeline (`panoId`, `heading`,
 * `verified`, `qualityTier`). The audience wants places they might *recognize*
 * — landmarks, famous cities, national parks — not anonymous roadsides.
 */
export interface GeoLocation {
  /** Stable kebab id, e.g. `eiffel-tower-paris`. Unique within a pack. */
  id: string;

  /** Where the panorama is. Runtime prefers `panoId`; falls back to lat/lng. */
  lat: number;
  lng: number;

  /**
   * The exact Google Street View panorama id at this spot. Filled by the
   * validation pipeline (resolved from the FREE metadata endpoint with
   * `source=outdoor`). ToS-permitted to store indefinitely. When present, the
   * runtime loads this exact pano; when absent, it resolves the nearest pano to
   * lat/lng (used by the hand-authored fixture before the pipeline runs).
   */
  panoId?: string;

  /** Initial camera heading in degrees (0–360), pointed at the interesting view. */
  heading?: number;
  /** Initial pitch in degrees (−90..90), default 0. */
  pitch?: number;

  // -- identity (plain English; shown in the reveal + place card) --
  /** Full human label: `"Eiffel Tower, Paris, France"`. */
  place: string;
  /** Short label for tight UI: `"Eiffel Tower"`. */
  shortName: string;

  /** ISO 3166-1 alpha-2, uppercase: `"FR"`, `"US"`. */
  countryCode: string;
  /** Plain country name: `"France"`. */
  country: string;
  continent: Continent;

  /** US two-letter state code (uppercase), e.g. `"AZ"` — present iff countryCode === 'US'. */
  stateCode?: string;
  /** Plain state name: `"Arizona"`. */
  state?: string;

  // -- reminiscence (CURATED landmarks only) --
  /**
   * 2–3 warm sentences for the post-guess place card. Teaches one nice fact and
   * opens a door to memory ("Did you ever visit?"). Present for CURATED landmarks
   * (the rich reveal); OMITTED for GENERATED regional locations, which get a
   * "light" reveal showing only the real place name ("Near Cody, Wyoming").
   */
  blurb?: string;

  /**
   * Difficulty tier — PRESENT for generated regional locations, ABSENT for
   * curated landmarks. Its presence is what marks a location as "generated"
   * (light reveal) vs "landmark" (rich reveal). See {@link isGenerated}.
   */
  difficulty?: Difficulty;

  // -- curation / quality --
  /**
   * Scope pools this location belongs to. ALWAYS includes `'world'` and the
   * location's `continent`; also its `countryCode`; for US locations also
   * `('US-' + stateCode)`; add `'landmarks'` for iconic must-knows. See
   * {@link poolsFor} for the canonical derivation.
   */
  pools: string[];

  /** Street View coverage generation. Ship only tier ≥ 3 (legible, modern). */
  qualityTier: 1 | 2 | 3 | 4;

  /** True once the metadata pipeline confirmed outdoor coverage at/near this point. */
  verified: boolean;
}

// ---------------------------------------------------------------------------
// A shippable pack
// ---------------------------------------------------------------------------

export interface LocationPack {
  /** Bump when the location ORDER changes — daily-seed indices depend on order. */
  version: string;
  /** ISO timestamp stamped by the pipeline at generation; optional for fixtures. */
  generatedAt?: string;
  locations: GeoLocation[];
}

// ---------------------------------------------------------------------------
// Scope addressing (the picker + the daily seed)
// ---------------------------------------------------------------------------

/**
 * What the player chose to play. Each scope is daily-seeded INDEPENDENTLY so the
 * World round and the "Italy" round don't collide on the same index.
 */
export type Scope =
  | { kind: 'world' }
  | { kind: 'landmarks' }
  | { kind: 'continent'; continent: Continent }
  | { kind: 'country'; countryCode: string }
  | { kind: 'usState'; stateCode: string };

/** The pool tag a scope draws from. Mirrors {@link poolsFor}. */
export function poolTagForScope(scope: Scope): string {
  switch (scope.kind) {
    case 'world':
      return 'world';
    case 'landmarks':
      return 'landmarks';
    case 'continent':
      return scope.continent;
    case 'country':
      return scope.countryCode.toUpperCase();
    case 'usState':
      return `US-${scope.stateCode.toUpperCase()}`;
  }
}

/** A stable string key for a scope — used in the daily seed and for routing. */
export function scopeKey(scope: Scope): string {
  return `${scope.kind}:${poolTagForScope(scope)}`;
}

/**
 * The daily-seed key for a scope at a difficulty. Regional scopes seed PER
 * difficulty so "France · Classic" and "France · Explorer" roll independently;
 * Landmarks has no difficulty and seeds on the scope alone.
 */
export function seedKey(scope: Scope, difficulty?: Difficulty): string {
  return difficulty ? `${scopeKey(scope)}:${difficulty}` : scopeKey(scope);
}

/** True if a location is a GENERATED regional location (vs a curated landmark). */
export function isGenerated(loc: Pick<GeoLocation, 'difficulty'>): boolean {
  return loc.difficulty != null;
}

/**
 * The canonical pool membership for a location. The pipeline + fixture MUST tag
 * each location with exactly this set so the picker and daily seed agree.
 */
export function poolsFor(loc: Pick<GeoLocation, 'continent' | 'countryCode' | 'stateCode'> & { landmark?: boolean }): string[] {
  const pools = ['world', loc.continent, loc.countryCode.toUpperCase()];
  if (loc.countryCode.toUpperCase() === 'US' && loc.stateCode) {
    pools.push(`US-${loc.stateCode.toUpperCase()}`);
  }
  if (loc.landmark) pools.push('landmarks');
  return pools;
}

// ---------------------------------------------------------------------------
// Scoring seam (implemented by atlas-data in `scoring.ts`)
// ---------------------------------------------------------------------------

/**
 * Coarse, ALWAYS-KIND proximity buckets. The game agent maps each bucket to warm
 * copy + visuals ("about a state over — lovely guess!"). There is deliberately
 * no "wrong" / "fail" bucket — the worst case is `farAfield`, framed as
 * curiosity, never failure.
 */
export type ProximityBucket =
  | 'bullseye' // essentially on it
  | 'sameArea' // same city / metro
  | 'sameRegion' // same state / a few hundred km
  | 'rightCountry'
  | 'rightContinent'
  | 'farAfield';

export interface RoundScore {
  /** Great-circle (haversine) distance, both units (US audience reads miles). */
  distanceKm: number;
  distanceMi: number;
  /** The standard 0–5000 curve, kept available but NOT shown raw to the player. */
  points: number;
  /** What the player actually sees: 1–5 stars. Never 0. */
  stars: 1 | 2 | 3 | 4 | 5;
  bucket: ProximityBucket;
}

/**
 * The scoring contract atlas-data implements in `scoring.ts`:
 *
 *   scoreGuess(guess, actual, mapDiagonalKm): RoundScore
 *   mapDiagonalKm(pool: GeoLocation[]): number   // bbox diagonal for the scope
 *
 * `points = 5000 * exp(-10 * d / D)` (d, D in the same unit), d via haversine
 * (R = 6371 km). Stars + bucket derive from distance so the surface stays warm.
 */
export type ScoreGuess = (guess: LatLng, actual: LatLng, mapDiagonalKm: number) => RoundScore;

// ---------------------------------------------------------------------------
// Validation — run in tests + at pack-build time
// ---------------------------------------------------------------------------

/** Returns a list of problems; an empty array means the pack is valid. */
export function validatePack(pack: LocationPack): string[] {
  const problems: string[] = [];
  if (!pack.version) problems.push('pack: missing version');
  if (!Array.isArray(pack.locations) || pack.locations.length === 0) {
    problems.push('pack: no locations');
    return problems;
  }

  const ids = new Set<string>();
  pack.locations.forEach((l, i) => {
    const at = `locations[${i}] (${l.id ?? '?'})`;
    if (!l.id) problems.push(`${at}: missing id`);
    else if (ids.has(l.id)) problems.push(`${at}: duplicate id`);
    else ids.add(l.id);

    if (!Number.isFinite(l.lat) || l.lat < -90 || l.lat > 90) problems.push(`${at}: bad lat ${l.lat}`);
    if (!Number.isFinite(l.lng) || l.lng < -180 || l.lng > 180) problems.push(`${at}: bad lng ${l.lng}`);

    if (!l.place) problems.push(`${at}: missing place`);
    if (!l.shortName) problems.push(`${at}: missing shortName`);
    // Curated landmarks need a blurb (rich reveal); generated locations (those
    // with a difficulty) intentionally omit it (light reveal).
    if (!l.blurb && !l.difficulty) problems.push(`${at}: missing blurb (curated landmark)`);
    if (l.difficulty && !DIFFICULTIES.includes(l.difficulty)) {
      problems.push(`${at}: bad difficulty ${l.difficulty}`);
    }

    if (!l.countryCode || l.countryCode.length !== 2) problems.push(`${at}: bad countryCode`);
    if (!l.country) problems.push(`${at}: missing country`);
    if (!CONTINENTS.includes(l.continent)) problems.push(`${at}: bad continent ${l.continent}`);

    if (l.countryCode?.toUpperCase() === 'US' && !l.stateCode) {
      problems.push(`${at}: US location missing stateCode`);
    }

    if (![1, 2, 3, 4].includes(l.qualityTier)) problems.push(`${at}: bad qualityTier`);

    if (!Array.isArray(l.pools) || l.pools.length === 0) problems.push(`${at}: empty pools`);
    else {
      if (!l.pools.includes('world')) problems.push(`${at}: pools must include 'world'`);
      if (!l.pools.includes(l.continent)) problems.push(`${at}: pools must include its continent`);
    }
  });

  return problems;
}
